import { Badge, Button, Popover, Select, Table, Text } from "@mantine/core";
import {
  JobTaskListSchema,
  JobTaskSchema,
  JobTaskStatus,
} from "@augmodo/lebron-browser-client";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  getTaskStatusColor,
  getTaskStatusText,
} from "@labeling-tools/ui-components/src/utils/helpers";
import { HEADER_HEIGHT } from "@/config/data";
import {
  IconArrowLeft,
  IconArrowRight,
  IconInfoCircle,
} from "@tabler/icons-react";
import { ERROR_CODES, type ErrorCodeEntry } from "@/config/error-codes";

export const AdminToolbar = ({
  jobTasks,
  activeTask,
  taskLabelFn,
}: {
  jobTasks: JobTaskListSchema;
  activeTask: JobTaskSchema;
  /** Optional custom label builder for the task dropdown. Receives task, index, and total count. */
  taskLabelFn?: (task: JobTaskSchema, index: number, total: number) => string;
}) => {
  const [searchParams] = useSearchParams();
  const taskId = searchParams.get("taskId");
  const navigate = useNavigate();

  if (!jobTasks?.tasks?.length) {
    return null;
  }

  const currentTaskIndex = jobTasks.tasks.findIndex(
    (task) => task.id === activeTask.id,
  );
  const previousTaskId = jobTasks.tasks[currentTaskIndex - 1]?.id;
  const nextTaskId = jobTasks.tasks[currentTaskIndex + 1]?.id;
  const total = jobTasks.tasks.length;
  const defaultLabel = (task: JobTaskSchema, index: number) =>
    `[${index + 1} / ${total}] ${task.id?.slice(task.id.length - 6)} | ${task.status}`;
  const data = jobTasks.tasks.map((task, index) => ({
    label: taskLabelFn
      ? taskLabelFn(task, index, total)
      : defaultLabel(task, index),
    value: task.id as string,
  }));

  return (
    <div
      className="flex flex-row items-center justify-between w-full bg-white border-b border-gray-200 p-4 fixed left-0 right-0 z-10"
      style={{ top: HEADER_HEIGHT, height: HEADER_HEIGHT }}
    >
      <Select
        size="sm"
        className="max-w-sm w-full"
        data={data || []}
        value={taskId}
        onChange={(value) => {
          if (value) {
            navigate(`${window.location.pathname}?taskId=${value}`);
          }
        }}
      />
      <div className="flex flex-row items-center gap-6">
        <Text size="sm" fw={500} c="gray.7" className="text-left">
          {activeTask.completed_by}
        </Text>
        <Badge
          color={getTaskStatusColor(activeTask.status as JobTaskStatus)}
          variant="light"
          size="sm"
        >
          {getTaskStatusText(activeTask.status as JobTaskStatus)}
        </Badge>

        {/* error code reference */}
        <Popover width={480} position="bottom-end" shadow="md" withArrow>
          <Popover.Target>
            <Button
              size="sm"
              variant="subtle"
              color="gray"
              leftSection={<IconInfoCircle size={16} />}
            >
              Error Codes
            </Button>
          </Popover.Target>
          <Popover.Dropdown>
            <Text size="sm" fw={600} mb="xs">
              Error Code Reference
            </Text>
            <Table fz="xs" striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Code</Table.Th>
                  <Table.Th>Description</Table.Th>
                  <Table.Th>Cause</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {ERROR_CODES.map((err: ErrorCodeEntry) => (
                  <Table.Tr key={err.code}>
                    <Table.Td style={{ whiteSpace: "nowrap" }}>
                      <Badge
                        variant="light"
                        color="red"
                        style={{ fontFamily: "monospace" }}
                      >
                        {err.code}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{err.description}</Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {err.cause}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Popover.Dropdown>
        </Popover>

        {/* task navigation */}
        <div className="flex flex-row items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            leftSection={<IconArrowLeft size={16} />}
            disabled={currentTaskIndex === 0}
            onClick={() => {
              navigate(`${window.location.pathname}?taskId=${previousTaskId}`);
            }}
          >
            Back
          </Button>
          <Text size="sm" fw={500} c="gray.7" className="text-left">
            {currentTaskIndex + 1} / {jobTasks.tasks.length}
          </Text>
          <Button
            size="sm"
            variant="outline"
            leftSection={<IconArrowRight size={16} />}
            disabled={currentTaskIndex === jobTasks.tasks.length - 1}
            onClick={() => {
              navigate(`${window.location.pathname}?taskId=${nextTaskId}`);
            }}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};
